import React from 'react';
import { useEffect } from 'react';
import { BarChart3, ShoppingBag, FileSpreadsheet, Receipt, RefreshCcw, Coins, Boxes, Wallet, Server, Users2, Clock, Zap } from 'lucide-react';
import Navbar from '../components/Navbar';
import PageHero from '../components/PageHero';
import GuaranteeCard from '../components/GuaranteeCard';
import Reviews from '../components/Reviews';
import FAQ from '../components/FAQ';
import { ContactSection, Footer } from '../components/Footer';

const painPoints = [
  { pain: 'Chasing marketplace reports every month', gain: 'We ingest Amazon, Flipkart, Meesho, Myntra reports weekly — no manual downloads' },
  { pain: 'GSTR-1 vs Sales report mismatches', gain: 'Auto-reconciliation of marketplace payouts against GST filings — line-item level' },
  { pain: 'Missed TCS credits & refunds', gain: 'We claim every rupee of TCS, TDS and marketplace fee refunds you\'re owed' },
  { pain: 'Blind on real profitability per SKU', gain: 'Monthly P&L that shows profit per SKU, channel and warehouse' },
  { pain: 'Fear of GST notices for platform sales', gain: 'Zero-notice track record — we handle SCNs and audits end-to-end' },
  { pain: 'Separate books for each state\'s GST', gain: 'Combined accounting for all your GSTINs in a single Tally company' },
  { pain: 'Books locked while accountant works', gain: 'Parallel Accounting on our dedicated Tally server — you and us work simultaneously' },
];

const services = [
  { Icon: FileSpreadsheet, title: 'Books of Accounts', desc: 'Zoho / Tally / QuickBooks setup and monthly bookkeeping tailored for e-commerce sellers.' },
  { Icon: RefreshCcw, title: 'Payment Reconciliation', desc: 'Match every marketplace payout with orders, returns, fees and TCS in one automated flow.' },
  { Icon: Receipt, title: 'GST + TCS Filing', desc: 'GSTR-1, 3B, 9 + TCS reconciliation across states. Nothing missed, nothing late.' },
  { Icon: BarChart3, title: 'SKU Profitability', desc: 'Know your true margin after fees, shipping, returns and RTO — per product, per channel.' },
  { Icon: ShoppingBag, title: 'Marketplace Advisory', desc: 'Guidance on FBA vs FBF, IXD, brand registry, and multi-state stock strategy for tax optimisation.' },
  { Icon: Coins, title: 'ITR & Advance Tax', desc: 'Quarterly advance tax computation, ITR filing, and year-end tax planning for seller entities.' },
  { Icon: Boxes, title: 'Inventory Accounting', desc: 'Weighted-average, FIFO or specific-identification — inventory valuation that survives audit.' },
  { Icon: Wallet, title: 'MIS & Investor Reports', desc: 'Monthly management dashboards ready for board, banker or investor conversations.' },
];

const tiers = [
  {
    name: 'Starter',
    price: '4,999',
    tag: 'Solo Sellers',
    features: [
      '1 User',
      'Monthly bookkeeping',
      'Grouped Sales Accounting',
      'Gold Tally Subscription included',
      'GSTR-1 & 3B filing (1 state)',
      'Monthly P&L',
      'Basic MIS dashboard',
    ],
    highlight: false,
  },
  {
    name: 'Growth',
    price: '9,999',
    tag: 'Scaling Brands',
    features: [
      'Everything in Starter',
      '2 Users',
      'GST filing up to 7 States',
      'Bill by Bill (Without Inventory)',
      'SKU-level profitability report',
      'TCS refund tracking',
      'TDS Return Filing',
      'Quarterly advance tax',
    ],
    highlight: true,
  },
  {
    name: 'Enterprise',
    price: '24,999',
    tag: 'D2C Brands & FBA Aggregators',
    features: [
      'Everything in Growth',
      '3 Users',
      'GST filing across more than 7 States',
      'Bill by Bill with Inventory',
      'Detailed Profitability Report',
      'Investor-grade MIS',
      'Dedicated CA + accountant',
      'ITR + Tax Audit (3CD)',
    ],
    highlight: false,
  },
];

const parallelBenefits = [
  { Icon: Server, title: 'Dedicated Tally Server', desc: 'Fast, always-on cloud server — never wait for someone to log out.' },
  { Icon: Users2, title: 'Simultaneous Access', desc: 'Multiple users can post entries at the same time without conflicts.' },
  { Icon: Clock, title: 'Real-time Sync', desc: 'Every entry is instantly visible to you and our team — no exports.' },
  { Icon: Zap, title: 'Zero Downtime', desc: 'Enterprise-grade backups and 99.9% uptime SLA.' },
];

// Split-screen visual for Parallel Accounting
const ParallelAccountingGraphic = () => (
  <div className="relative bg-neutral-950 rounded-3xl border-2 border-[#F39200]/30 p-6 md:p-10 overflow-hidden">
    {/* server glow */}
    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full blur-3xl opacity-40 pointer-events-none" style={{ background: 'radial-gradient(closest-side, #F39200, transparent 70%)' }} />

    <div className="relative grid md:grid-cols-[1fr_auto_1fr] gap-6 md:gap-8 items-stretch">
      {/* Left: You (client) */}
      <div className="bg-white/[0.03] backdrop-blur border border-white/10 rounded-2xl p-6">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-white/10 text-white flex items-center justify-center text-xs font-bold">YOU</div>
          <div className="text-[11px] uppercase tracking-wider text-neutral-400 font-semibold">Client side</div>
        </div>
        <div className="mt-4 text-xl font-bold text-white">Daily offline books</div>
        <ul className="mt-4 space-y-2.5 text-sm text-neutral-300">
          <li className="flex items-start gap-2"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#F39200] shrink-0" /> Cash & bank entries</li>
          <li className="flex items-start gap-2"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#F39200] shrink-0" /> Vendor bills & payments</li>
          <li className="flex items-start gap-2"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#F39200] shrink-0" /> Salary & operational expenses</li>
          <li className="flex items-start gap-2"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#F39200] shrink-0" /> Petty cash & reimbursements</li>
        </ul>
      </div>

      {/* Center: Server */}
      <div className="flex flex-col items-center justify-center">
        <div className="hidden md:block w-px h-6 bg-gradient-to-b from-transparent to-[#F39200]/50" />
        <div className="relative">
          <div className="w-24 h-24 md:w-28 md:h-28 rounded-2xl bg-[#F39200] text-white flex flex-col items-center justify-center shadow-[0_0_60px_rgba(243,146,0,0.6)]">
            <Server size={28} />
            <div className="mt-1 text-[10px] font-bold uppercase tracking-widest">Tally Server</div>
          </div>
          {/* pulse rings */}
          <div className="absolute inset-0 rounded-2xl ring-2 ring-[#F39200]/40 animate-ping pointer-events-none" />
        </div>
        <div className="hidden md:block w-px h-6 bg-gradient-to-t from-transparent to-[#F39200]/50" />
        <div className="mt-3 text-[11px] uppercase tracking-widest text-[#F39200] font-bold">Live Sync</div>
      </div>

      {/* Right: EasyWorks */}
      <div className="bg-white/[0.03] backdrop-blur border border-white/10 rounded-2xl p-6">
        <div className="flex items-center gap-2 justify-end">
          <div className="text-[11px] uppercase tracking-wider text-neutral-400 font-semibold">EasyWorks side</div>
          <div className="w-8 h-8 rounded-lg bg-[#F39200] text-white flex items-center justify-center text-[10px] font-bold">EW</div>
        </div>
        <div className="mt-4 text-xl font-bold text-white text-right md:text-right">E-commerce books</div>
        <ul className="mt-4 space-y-2.5 text-sm text-neutral-300 md:text-right">
          <li className="flex items-start gap-2 md:justify-end md:flex-row-reverse"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#F39200] shrink-0" /> Amazon / Flipkart payouts</li>
          <li className="flex items-start gap-2 md:justify-end md:flex-row-reverse"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#F39200] shrink-0" /> Marketplace fees & TCS</li>
          <li className="flex items-start gap-2 md:justify-end md:flex-row-reverse"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#F39200] shrink-0" /> GSTR-1 / 3B filings</li>
          <li className="flex items-start gap-2 md:justify-end md:flex-row-reverse"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#F39200] shrink-0" /> Multi-state GST reconciliation</li>
        </ul>
      </div>
    </div>
  </div>
);

const ParallelAccounting = () => (
  <section className="bg-white py-16 md:py-20" data-testid="parallel-accounting">
    <div className="max-w-7xl mx-auto px-5 lg:px-10">
      <div className="max-w-3xl">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-[#F39200] font-semibold mb-3">
          <span className="w-2 h-2 rounded-full bg-[#F39200] animate-pulse" /> Exclusive to EasyWorks
        </div>
        <h2 className="text-3xl md:text-5xl font-extrabold text-neutral-900 leading-[1.05]">
          Parallel Accounting on our{' '}
          <span className="relative inline-block text-[#F39200]">Dedicated Tally Server
            <svg className="absolute -bottom-2 left-0 w-full" height="10" viewBox="0 0 480 10" fill="none">
              <path d="M2 7 C 120 1, 280 1, 478 6" stroke="#F39200" strokeWidth="2" strokeLinecap="round" fill="none" />
            </svg>
          </span>
        </h2>
        <p className="mt-5 text-[15px] md:text-base text-neutral-600 max-w-2xl leading-relaxed">
          Your team handles daily offline books. We handle the marketplace, GST and payout reconciliation. Both work in the same Tally company at the same time — on our high-speed dedicated cloud server. No exports, no waiting.
        </p>
      </div>

      <div className="mt-10">
        <ParallelAccountingGraphic />
      </div>

      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {parallelBenefits.map(({ Icon, title, desc }) => (
          <div key={title} className="bg-[#FAF2E8]/60 border border-black/5 rounded-2xl p-6 hover:border-[#F39200] transition-colors">
            <div className="w-11 h-11 rounded-xl bg-white shadow-sm text-[#F39200] flex items-center justify-center">
              <Icon size={20} />
            </div>
            <div className="mt-4 text-base font-semibold text-neutral-900">{title}</div>
            <p className="mt-2 text-sm text-neutral-600 leading-relaxed">{desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default function EcomAccountingPage() {
  useEffect(() => {
    document.title = 'E-Commerce Accounting Services for Amazon, Flipkart & Meesho Sellers | EasyWorks';
    window.scrollTo(0, 0);
  }, []);
  return (
    <div className="min-h-screen bg-[#FAF2E8] font-[Inter]" data-testid="ecom-accounting-page">
      <Navbar />
      <PageHero
        eyebrow="E-Commerce Accounting"
        headline="Books that actually match your"
        headlineAccent="marketplace payouts"
        description="Purpose-built accounting for Amazon, Flipkart, Meesho, Myntra and D2C sellers. From payout reconciliation to GST + TCS and SKU-level profitability — one team, transparent pricing."
        category="ecom_accounting"
        subtype="General"
        service="E-Commerce Accounting"
        formTitle="Get a free consultation"
        formSubtitle="We'll audit your last month's books free of cost."
        formTestId="ecom-quote-form"
        ctaLabel="Book Free Audit"
      />

      {/* Comparison table */}
      <section className="bg-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <div className="text-xs uppercase tracking-wider text-[#F39200] font-semibold mb-3">Why sellers switch to EasyWorks</div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-neutral-900 max-w-3xl leading-[1.15]">
            Generic accountants weren&apos;t built for marketplace sellers.{' '}
            <span className="text-[#F39200]">We are.</span>
          </h2>

          <div className="mt-10 rounded-2xl overflow-hidden border border-black/5 bg-white shadow-sm">
            <div className="hidden md:grid grid-cols-[1fr_1fr] text-sm">
              <div className="p-5 font-semibold bg-neutral-50 text-neutral-500 uppercase tracking-wide text-xs">Generic accountant</div>
              <div className="p-5 font-semibold bg-[#F39200] text-white uppercase tracking-wide text-xs flex items-center gap-2">
                <span>EasyWorks</span>
                <span className="text-white/70 normal-case tracking-normal">— built for marketplace sellers</span>
              </div>
              {painPoints.map((p, i) => (
                <React.Fragment key={p.pain}>
                  <div className={`p-5 border-t border-black/5 flex items-start gap-3 ${i % 2 === 0 ? 'bg-white' : 'bg-neutral-50/60'}`}>
                    <span className="mt-1 w-5 h-5 rounded-full bg-neutral-100 text-neutral-400 flex items-center justify-center shrink-0 font-bold text-xs">✕</span>
                    <span className="text-neutral-600 line-through decoration-neutral-300/70">{p.pain}</span>
                  </div>
                  <div className={`p-5 border-t border-black/5 border-l border-black/5 flex items-start gap-3 ${i % 2 === 0 ? 'bg-[#F39200]/[0.05]' : 'bg-[#F39200]/[0.08]'}`}>
                    <span className="mt-1 w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0 font-bold text-xs">✓</span>
                    <span className="text-neutral-900 font-medium">{p.gain}</span>
                  </div>
                </React.Fragment>
              ))}
            </div>
            <div className="md:hidden divide-y divide-black/5">
              {painPoints.map((p) => (
                <div key={p.pain} className="p-5">
                  <div className="flex items-start gap-2 text-sm">
                    <span className="mt-1 w-4 h-4 rounded-full bg-neutral-100 text-neutral-400 flex items-center justify-center shrink-0 font-bold text-[10px]">✕</span>
                    <div>
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400">Generic accountant</div>
                      <div className="text-neutral-600 line-through decoration-neutral-300/70">{p.pain}</div>
                    </div>
                  </div>
                  <div className="mt-3 flex items-start gap-2 text-sm bg-[#F39200]/[0.06] rounded-lg p-3">
                    <span className="mt-1 w-4 h-4 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0 font-bold text-[10px]">✓</span>
                    <div>
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-[#F39200]">EasyWorks</div>
                      <div className="text-neutral-900 font-medium">{p.gain}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <ParallelAccounting />

      {/* Services grid */}
      <section className="bg-[#FAF2E8] py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <div className="text-xs uppercase tracking-wider text-[#F39200] font-semibold mb-3">What we do</div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-neutral-900">Full-stack e-commerce accounting</h2>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {services.map(({ Icon, title, desc }) => (
              <div key={title} className="bg-white rounded-2xl border border-black/5 p-6 hover:border-[#F39200] hover:shadow-md transition-all">
                <div className="w-12 h-12 rounded-xl bg-[#F39200]/10 text-[#F39200] flex items-center justify-center">
                  <Icon size={22} />
                </div>
                <div className="mt-4 text-lg font-semibold text-neutral-900">{title}</div>
                <p className="mt-2 text-sm text-neutral-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing tiers */}
      <section className="bg-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <div className="text-xs uppercase tracking-wider text-[#F39200] font-semibold mb-3">Pricing</div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-neutral-900">Plans that scale with your GMV</h2>
          <p className="mt-3 text-sm text-neutral-500">All prices exclusive of GST. Custom enterprise plans available.</p>
          <div className="mt-8 grid md:grid-cols-3 gap-5">
            {tiers.map((t) => (
              <div key={t.name} className={`rounded-2xl border p-7 ${t.highlight ? 'border-[#F39200] shadow-lg bg-white relative' : 'border-black/5 bg-white shadow-sm'}`}>
                {t.highlight && <span className="absolute -top-3 right-6 bg-[#F39200] text-white text-[11px] font-semibold px-3 py-1 rounded-full uppercase tracking-wide">Most Popular</span>}
                <div className="text-xs uppercase tracking-wide text-neutral-500 font-semibold">{t.name}</div>
                <div className="mt-1 text-[11px] font-semibold text-[#F39200]">{t.tag}</div>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="text-[11px] uppercase tracking-wide text-neutral-500 font-semibold">Starting from</span>
                </div>
                <div className="mt-1 text-3xl font-extrabold text-neutral-900">₹{t.price}<span className="text-sm text-neutral-500 font-medium">/month</span></div>
                <a href="#ecom-quote-form" className={`mt-5 w-full inline-flex items-center justify-center gap-2 h-11 rounded-lg text-sm font-semibold transition-colors ${t.highlight ? 'bg-[#F39200] hover:bg-[#E08400] text-white' : 'bg-neutral-900 hover:bg-neutral-800 text-white'}`}>
                  Get Started
                </a>
                <ul className="mt-6 space-y-2.5">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-neutral-700">
                      <span className="mt-1 w-4 h-4 rounded-full bg-[#F39200]/10 text-[#F39200] flex items-center justify-center shrink-0 font-bold text-[10px]">✓</span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <GuaranteeCard />
      <Reviews />
      <FAQ />
      <ContactSection />
      <Footer />
    </div>
  );
}
